import React, { useState, useRef, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import './Pages/PagesCss/NavBar.css';
import logo from './assets/logo.png';

const NavBar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [mobileSubMenu, setMobileSubMenu] = useState(null);
  const navRef = useRef(null);
  const location = useLocation();

  const toggleDropdown = (index) => {
    setDropdownOpen(dropdownOpen === index ? null : index);
  };

  const toggleMobile = () => setMobileOpen(!mobileOpen);

  const closeMobileMenu = () => {
    setMobileOpen(false);
    setDropdownOpen(null);
    setMobileSubMenu(null);
  };

  const openMobileSubMenu = (index) => setMobileSubMenu(index);
  const closeMobileSubMenu = () => setMobileSubMenu(null);

  useEffect(() => {
    closeMobileMenu();
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setDropdownOpen(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyDown = (event, index) => {
    if (event.key === 'Escape') {
      setDropdownOpen(null);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleDropdown(index);
    }
  };

  const menuItems = [
    { name: "Home", path: "/" },
    {
      name: "BullCalc", 
      dropdown: [
        { title: "Calculators", links: [
        { name: "Profit Calculator", path: "/StockProfitCalculator" },
        { name: "Position Profit Calculator", path: "/ShortLongCalculator" },
        { name: "Dividend Profit Calculator", path: "/DividendProfitCalculator" },
      ]},
      { title: "Suite", links: [
        { name: "Average Price", path: "/AveragePriceCalculator" },
        { name: "Stock Breakeven", path: "/StockBreakevenCalculator" },
        { name: "Target Price", path: "/TargetPriceCalculator" },
      ]},
      { title: "Tools", links: [
        { name: "Quantity Estimator", path: "/QuantityEstimatorCalculator" },
        { name: "Max Buy Calculator", path: "/MaxBuyCalculator" },
        { name: "Lot Size Finder", path: "/LotSizeFinderCalculator" },
      ]}
    ]
  },
    {
      name: "Investo",
      dropdown: [
        { title: "Calculators", links: [
        { name: "SIP Calculator", path: "/SIPCalculator" },
        { name: "Portfolio Calculator", path: "/StockPortfolioCalculator" },
        { name: "Retirement Calculator", path: "/RetirementInvestmentCalculator" },
      ]},
      { title: "Suite", links: [
        { name: "Lump Sum Returns", path: "/LumpSumReturnsCalculator" },
        { name: "Inflation Impact", path: "/InflationImpactCalculator" },
        { name: "Goal Planner", path: "/GoalPlannerCalculator" },
      ]},
      { title: "Tools", links: [
        { name: "Wealth Projection", path: "/WealthProjectionCalculator" },
        { name: "Retirement Gap Finder", path: "/RetirementGapFinderCalculator" },
        { name: "Compounding Visualizer", path: "/CompoundingVisualizerCalculator" },
      ]}
    ]
  },
    { 
      name: "StopLoss",
      dropdown: [
        { title: "Calculators", links: [
        { name: "Stop Loss Calculator", path: "/StopLossCalculator" },
        { name: "Risk/Reward Calculator", path: "/RiskRewardCalculator" },
        { name: "Stop Loss Target Calculator", path: "/StopLossTargetCalculator" },
      ]},
      { title: "Suite", links: [
        { name: "Position Size", path: "/PositionSizeCalculator" },
        { name: "Stock Volatility Risk", path: "/StockVolatilityRiskCalculator" },
        { name: "Max Loss Calculator", path: "/MaxLossCalculator" },
      ]},
      { title: "Tools", links: [
        { name: "Trade Risk Analyzer", path: "/TradeRiskAnalyzer" },
        { name: "Risk Per Trade Finder", path: "/RiskPerTradeFinder" },
        { name: "Risk Exposure Meter", path: "/RiskExposureMeterCalculator" },
      ]}
    ]
  },
    {
      name: "Taxify",
      dropdown: [
        { title: "Calculators", links: [
        { name: "Capital Gains Calculator", path: "/StockCapitalGainsCalculator" },
        { name: "Brokerage Calculator", path: "/BrokerageCalculator" },
        { name: "Dividend Tax Calculator", path: "/DividendTaxCalculator" },
      ]},
      { title: "Suite", links: [
        { name: "Tax Estimator", path: "/TaxEstimatorCalculator" },
        { name: "Net Profit After Tax", path: "/NetProfitAfterTaxCalculator" },
        { name: "Post-Tax Return Calculator", path: "/PostTaxReturnCalculator" },
      ]},
      { title: "Tools", links: [
        { name: "Tax Bracket Finder", path: "/TaxBracketFinder" },
        { name: "Effective Tax Rate", path: "/EffectiveTaxRateCalculator" },
        { name: "Tax Deductions Calculator", path: "/TaxDeductionsCalculator" }
      ]}
    ]
    },
    { name: "Blog", path: "https://blog.noobtobull.com" },
  ];

  return (
    <header className="navigation" ref={navRef} role="navigation">
      <div className="nav-container">
        <NavLink 
          to="/" 
          className="brand" 
          onClick={closeMobileMenu}
          aria-label="NoobToBull Home"
        >
          <img src={logo} alt="NoobToBull Logo" className="logo" />
          <span className="brand-text">NoobToBull</span>
        </NavLink>

        {/* Mobile menu button */}
        <button 
          className={`nav-mobile ${mobileOpen ? "active" : ""}`}
          onClick={toggleMobile}
          aria-expanded={mobileOpen}
          aria-label="Toggle navigation menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        {/* Desktop Navigation */}
        <nav className="desktop-nav">
          <div className="nav-menu-container">
          <ul className="nav-menu">
            {menuItems.map((item, idx) => (
              <li key={idx} className="nav-item">
                {item.dropdown ? (
                  <>
                    <button 
                      className={`nav-link dropdown-btn ${dropdownOpen === idx ? "active" : ""}`}
                      onClick={() => toggleDropdown(idx)}
                      onKeyDown={(e) => handleKeyDown(e, idx)}
                      aria-expanded={dropdownOpen === idx}
                      aria-haspopup="true"
                    >
                      {item.name} 
                      <span className={`arrow ${dropdownOpen === idx ? "rotate" : ""}`} aria-hidden="true">
                        &#9660;
                      </span>
                    </button>
                    <div 
                      className={`nav-dropdown ${dropdownOpen === idx ? "show" : ""}`}
                      role="menu"
                    >
                      <div className="dropdown-content">
                        {item.dropdown.map((section, sIdx) => (
                          <div className="dropdown-section" key={sIdx}>
                            <h4>{section.title}</h4>
                            <div className="dropdown-links">
                              {section.links.map((link, lIdx) => (
                                <NavLink 
                                  key={lIdx}
                                  to={link.path}
                                  onClick={closeMobileMenu}
                                  className={`dropdown-item ${location.pathname === link.path ? 'active' : ''}`}
                                  role="menuitem"
                                >
                                  {link.name}
                                </NavLink>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                ) : item.path.startsWith("http") ? (
                  <a 
                    href={item.path} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="nav-link external-link"
                  >
                    {item.name}
                  </a>
                ) : (
                  <NavLink 
                    to={item.path} 
                    className="nav-link"
                    onClick={closeMobileMenu}
                  >
                    {item.name}
                  </NavLink>
                )}
              </li>
            ))}
          </ul>
          </div>
        </nav>

        {/* Mobile Navigation */}
        <nav className={`mobile-nav ${mobileOpen ? "open" : ""}`}>
          {mobileSubMenu === null ? (
            <ul className="mobile-menu">
              {menuItems.map((item, idx) => (
                <li key={idx} className="mobile-nav-item">
                  {item.dropdown ? (
                    <button
                      className="mobile-nav-link mobile-dropdown-btn"
                      onClick={() => openMobileSubMenu(idx)}
                    >
                      <span>{item.name}</span>
                      <span className="mobile-arrow">&#9654;</span>
                    </button>
                  ) : item.path.startsWith("http") ? (
                    <a 
                      href={item.path} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="mobile-nav-link external-link"
                      onClick={closeMobileMenu}
                    >
                      {item.name}
                    </a>
                  ) : (
                    <NavLink 
                      to={item.path} 
                      className="mobile-nav-link"
                      onClick={closeMobileMenu}
                    >
                      {item.name}
                    </NavLink>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <div className="mobile-submenu">
              <button 
                className="mobile-back-btn" 
                onClick={closeMobileSubMenu}
              >
                <span className="back-arrow">←</span>
                Back to Menu
              </button>
              <div className="mobile-submenu-content">
                {menuItems[mobileSubMenu].dropdown.map((section, sIdx) => (
                  <div key={sIdx} className="mobile-submenu-section">
                    <h4 className="mobile-submenu-title">{section.title}</h4>
                    <div className="mobile-submenu-links">
                      {section.links.map((link, lIdx) => (
                        <NavLink
                          key={lIdx}
                          to={link.path}
                          className={`mobile-submenu-link ${location.pathname === link.path ? 'active' : ''}`}
                          onClick={closeMobileMenu}
                        >
                          {link.name}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </nav>
      </div>
    </header>
  );
};

export default NavBar;